import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { CircleCheckBig, UploadIcon } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";

type FileDropZoneProps = {
  acceptedFileFormats: string;
  clearSelectedFile: () => void;
  documentationLink?: string;
  handleDrop: (e: React.DragEvent) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedFile?: File;
};

export const FileDropZone = ({
  acceptedFileFormats,
  clearSelectedFile,
  documentationLink,
  handleDrop,
  handleFileChange,
  selectedFile,
}: FileDropZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!selectedFile) {
      const fileInput = fileInputRef.current;
      if (fileInput) {
        fileInput.value = "";
      }
    }
  }, [selectedFile]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const fileOnDropIsSupported = (fileName?: string) => {
    let supported = false;

    const formats = acceptedFileFormats.split(",");

    formats.forEach((format) => {
      if (isDragging && fileName?.endsWith(format)) {
        supported = true;
      }
    });

    return supported;
  };

  return (
    <div className="min-w-0 max-w-full space-y-2">
      <div
        className={clsx(
          "max-w-full min-w-0 cursor-pointer rounded-lg border-2 border-dashed p-8 text-center",
          {
            "border-primary": !selectedFile,
            "bg-blue-50": isDragging,
            "bg-green-50 border-green-600": selectedFile,
          },
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(e) => {
          e.preventDefault();

          const file = e.dataTransfer.files?.[0];
          if (!fileOnDropIsSupported(file?.name)) {
            setIsDragging(false);
            toast.error(`Only files with format: '${acceptedFileFormats}' are allowed.`);
            return;
          }

          handleDrop(e);
          setIsDragging(false);
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFileFormats}
          className="hidden"
          onChange={handleFileChange}
        />
        {selectedFile ? (
          <div className="flex w-full min-w-0 flex-col items-center space-y-2">
            <CircleCheckBig className="text-green-600 h-8 w-8" />
            <p className="max-w-full break-all text-sm font-medium text-green-700">
              {selectedFile.name}
            </p>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                clearSelectedFile();
              }}
            >
              Clear selection
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <UploadIcon className="text-muted-foreground h-8 w-8" />
            <p className="text-sm text-muted-foreground">
              Drag and drop file here, or{" "}
              <span className="underline font-semibold">choose file</span>
            </p>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-0 break-words text-xs text-muted-foreground">
          Supported formats: "{acceptedFileFormats}"
        </p>
        {documentationLink ? (
          <a href={documentationLink} className="text-xs text-primary underline" target="_blank">
            View Documentation
          </a>
        ) : null}
      </div>
    </div>
  );
};
